<template>
  <header class="my-downloads-header">
    <div class="title-row">
      <button
        type="button"
        class="back-btn"
        aria-label="Voltar"
        @click="$emit('back')"
      >
        <i class="material-icons">arrow_back</i>
      </button>
      <h1 class="title">Meus downloads</h1>
      <button
        type="button"
        class="settings-btn"
        aria-label="Configurações de download"
        @click="$emit('open-settings')"
      >
        <i class="material-icons">settings</i>
      </button>
    </div>
    <p class="subtitle">Disponíveis offline neste aparelho</p>

    <div
      v-if="totalLearningObjects > 0"
      class="stats-card"
      :style="{ borderColor: hexAlpha($theme.primaryColor, 0.18), backgroundColor: hexAlpha($theme.primaryColor, 0.06) }"
    >
      <button
        type="button"
        class="stats-toggle"
        :aria-expanded="expanded ? 'true' : 'false'"
        @click="expanded = !expanded"
      >
        <span class="stats-label">Conteúdo offline</span>
        <span class="stats-value" :style="{ color: $theme.primaryColor }">
          {{ totalLearningObjects }} {{ totalLearningObjects === 1 ? 'aula' : 'aulas' }}
          ·
          {{ totalCourses }} {{ totalCourses === 1 ? 'curso' : 'cursos' }}
        </span>
        <i
          class="material-icons stats-chevron"
          :class="{ 'stats-chevron--open': expanded }"
          :style="{ color: $theme.primaryColor }"
        >expand_more</i>
      </button>

      <transition name="stats-expand">
        <div v-if="expanded" class="stats-details">
          <div class="stats-row">
            <span class="stats-row-label">Espaço usado</span>
            <span class="stats-row-value">{{ formattedSize }}</span>
          </div>
        </div>
      </transition>
    </div>
  </header>
</template>

<script>
import IconMenuDownload from '@/components/UI/IconMenuDownload.vue';
import { formatBytes } from '@/services/download/downloadHelpers';

// Converte #RRGGBB em rgba(R,G,B,alpha). Tolera input já-rgba pra passar sem
// quebrar. Usado pra montar fundos translúcidos a partir da cor primária do
// tema sem inflar o SCSS com várias variantes.
function hexToRgba (hex, alpha) {
  if (!hex || typeof hex !== 'string') return `rgba(255,255,255,${alpha})`;
  if (hex.startsWith('rgb')) return hex;
  const clean = hex.replace('#', '');
  const full = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default {
  name: 'MyDownloadsHeader',
  components: { IconMenuDownload },
  props: {
    totalLearningObjects: { type: Number, default: 0 },
    totalCourses: { type: Number, default: 0 },
    totalBytes: { type: Number, default: 0 }
  },
  data() {
    return {
      // Expandido = mostra detalhes (espaço usado, futuros). Default colapsado
      // pra manter o header compacto. Estado local — não persiste entre sessões;
      // cada visita à página começa colapsado (decisão de UX: detalhes são
      // info secundária, não merece ocupar espaço por padrão).
      expanded: false
    };
  },
  computed: {
    formattedSize() {
      if (!this.totalBytes) return '—';
      return formatBytes(this.totalBytes);
    }
  },
  methods: {
    hexAlpha: hexToRgba
  }
};
</script>

<style scoped lang="scss">
.my-downloads-header {
  padding: 20px 18px 14px;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform: scale(1.2);
}

.title {
  margin: 0;
  font-size: 22px;
  font-weight: 500;
  color: var(--md-text);
  line-height: 1.2;
}

/* Espelha o .settings-btn: mesma área de toque (38px) e mesmo peso visual,
   pra o header não ficar com dois botões de pesos diferentes nas pontas. */
.back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  margin-left: -8px;
  border: none;
  background: transparent;
  border-radius: 50%;
  color: var(--md-text);
  cursor: pointer;
  transition: background 0.15s;
}

.settings-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: none;
  background: transparent;
  border-radius: 50%;
  color: var(--md-text-dim);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  i { font-size: 22px; }

  &:active {
    background: var(--md-row-active, rgba(0, 0, 0, 0.05));
    color: var(--md-text);
  }
}

.subtitle {
  margin: 6px 0 14px;
  font-size: 13px;
  color: var(--md-text-dim);
}

.stats-card {
  border-radius: 12px;
  border: 1px solid;
  font-size: 13px;
  overflow: hidden;  // contém a transição expand sem vazar o radius
}

.stats-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;

  .stats-label {
    color: var(--md-text-dim);
    flex-shrink: 0;
  }

  .stats-value {
    font-weight: 600;
    flex: 1;
    text-align: right;
  }

  .stats-chevron {
    font-size: 20px;
    transition: transform 0.2s ease;
    flex-shrink: 0;

    &--open {
      transform: rotate(180deg);
    }
  }
}

.stats-details {
  padding: 0 14px 12px;
}

.stats-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid var(--md-border, rgba(0, 0, 0, 0.08));
  font-size: 13px;

  & + .stats-row {
    margin-top: 4px;
  }
}

.stats-row-label {
  color: var(--md-text-dim);
}

.stats-row-value {
  font-weight: 600;
  color: var(--md-text);
}

// Transição de expand: altura cresce de 0 → conteúdo. CSS-only via max-height
// porque não dá pra animar `height: auto`. 200px é teto seguro pra 1-3 linhas
// (cresce conforme adicionarmos mais detalhes futuros).
.stats-expand-enter-active,
.stats-expand-leave-active {
  transition: max-height 0.22s ease, opacity 0.18s ease;
  overflow: hidden;
}

.stats-expand-enter,
.stats-expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.stats-expand-enter-to,
.stats-expand-leave {
  max-height: 200px;
  opacity: 1;
}
</style>
